<script lang="ts">
  import { onMount } from 'svelte';
  import Screen from '../lib/design/Screen.svelte';
  import { link, href } from '../lib/router.svelte';
  import { compact, count, usd } from '../lib/format';
  import {
    getUsage,
    getUsageDaily,
    getUsageRecent,
    getUsageByModel,
    type Usage,
    type DailyPoint,
    type RecentCall,
    type ModelRow,
  } from '../lib/usage';

  let usage = $state<Usage | null>(null);
  let daily = $state<DailyPoint[]>([]);
  let recent = $state<RecentCall[]>([]);
  let models = $state<ModelRow[]>([]);
  let err = $state(false);

  onMount(async () => {
    try {
      [usage, daily, recent, models] = await Promise.all([
        getUsage(),
        getUsageDaily(30),
        getUsageRecent(),
        getUsageByModel(),
      ]);
    } catch {
      err = true;
    }
  });

  const capPct = $derived.by(() => {
    if (!usage || !usage.limits.monthly_token_cap) return null;
    return Math.min(
      100,
      Math.round((usage.usage.total_tokens / usage.limits.monthly_token_cap) * 100),
    );
  });

  const period = $derived.by(() => {
    if (!usage) return '';
    const d = (iso: string) =>
      new Date(iso).toLocaleDateString('en', { month: 'short', day: 'numeric' });
    return `${d(usage.period.start)} – ${d(usage.period.end)}`;
  });

  const resetsOn = $derived(
    usage
      ? new Date(usage.period.end).toLocaleDateString('en', { month: 'long', day: 'numeric' })
      : '',
  );

  const tokensLine = $derived.by(() => {
    if (!usage) return '';
    const used = `${count(usage.usage.total_tokens)} tokens used`;
    const cap = usage.limits.monthly_token_cap;
    if (!cap) return `${used} · no monthly cap`;
    const left = usage.remaining.tokens;
    return left === null
      ? `${used} of ${count(cap)}`
      : `${used} of ${count(cap)} · ${count(left)} left`;
  });

  // Daily token bars, normalized into the viewBox.
  const W = 320;
  const H = 72;
  const bars = $derived.by(() => {
    if (!daily.length) return [];
    const max = Math.max(1, ...daily.map((d) => d.total_tokens));
    const bw = W / daily.length;
    return daily.map((d, i) => {
      const h = (d.total_tokens / max) * (H - 2);
      return { x: i * bw, y: H - h, w: Math.max(bw - 1, 1), h: Math.max(h, 0.5), d };
    });
  });

  const short = (iso: string) =>
    new Date(iso).toLocaleString('en', { month: 'short', day: 'numeric', hour: 'numeric' });
</script>

<Screen title="Usage & billing">
  {#if err}
    <p class="hint">Couldn't load usage. Check your connection and try again.</p>
  {:else if !usage}
    <p class="hint">Loading…</p>
  {:else}
    <div class="card lead">
      <p class="eyebrow">Managed backend · {period}</p>
      <p class="plan">{usage.plan} plan</p>
      <p class="spend">{usd(usage.usage.cost_usd)}</p>
      <p class="sub">spent this period</p>

      {#if capPct !== null}
        <div class="bar"><span style="width:{capPct}%"></span></div>
      {/if}
      <p class="sub">{tokensLine}</p>
    </div>

    <div class="stats">
      <div class="stat">
        <span class="k">Calls</span><span class="v">{count(usage.usage.calls)}</span>
      </div>
      <div class="stat">
        <span class="k">Tokens in</span><span class="v">{compact(usage.usage.input_tokens)}</span>
      </div>
      <div class="stat">
        <span class="k">Tokens out</span><span class="v">{compact(usage.usage.output_tokens)}</span>
      </div>
      <div class="stat">
        <span class="k">Rate limit</span><span class="v">{usage.limits.rate_limit_per_min}/min</span
        >
      </div>
    </div>

    <section>
      <h2>Last {daily.length} days</h2>
      {#if bars.length}
        <svg
          class="chart"
          viewBox="0 0 {W} {H}"
          preserveAspectRatio="none"
          role="img"
          aria-label="Daily token usage"
        >
          {#each bars as b (b.d.day)}
            <rect x={b.x} y={b.y} width={b.w} height={b.h}>
              <title>{b.d.day}: {count(b.d.total_tokens)} tokens · {usd(b.d.cost_usd)}</title>
            </rect>
          {/each}
        </svg>
      {:else}
        <p class="hint">No calls in this window.</p>
      {/if}
    </section>

    {#if models.length}
      <section>
        <h2>By model</h2>
        <ul class="rows">
          {#each models as m, i (i)}
            <li>
              <span class="who">{m.model ?? 'unknown'}</span>
              <span class="meta">{count(m.calls)} calls · {compact(m.total_tokens)} tok</span>
              <span class="cost">{usd(m.cost_usd)}</span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}

    <section>
      <h2>Recent calls</h2>
      {#if recent.length}
        <ul class="rows calls">
          {#each recent.slice(0, 40) as c, i (i)}
            <li class:failed={!c.ok}>
              <span class="who">
                <span class="workload">{c.workload ?? c.phase ?? '—'}</span>
                <span class="when">{short(c.timestamp)} · {c.upstream_model ?? '—'}</span>
              </span>
              <span class="meta">
                {#if c.ok}{compact(c.total_tokens)} tok{:else}{c.error_code ?? 'error'}{/if}
              </span>
              <span class="cost">{usd(c.cost_usd)}</span>
            </li>
          {/each}
        </ul>
      {:else}
        <p class="hint">No calls yet.</p>
      {/if}
    </section>

    <section>
      <h2>Billing</h2>
      <div class="card">
        <p class="value">{usage.plan} plan · resets {resetsOn}</p>
        <p class="muted">
          Managed-backend calls are metered to this plan and billed by Kip — there's no invoice to
          settle here. Your plan is provisioned with your account.
        </p>
        <p class="muted">
          Provider keys you use directly in the desktop app (BYOK) never reach this backend, so
          they're unmetered and don't appear in these numbers.
        </p>
      </div>
    </section>
  {/if}

  <a class="back" use:link href={href('/settings')}>← Settings</a>
</Screen>

<style>
  .card {
    border: 1px solid var(--line);
    border-radius: 14px;
    padding: 16px;
    background: var(--cream-2);
    box-shadow: var(--shadow-soft);
  }
  .lead {
    margin-bottom: 16px;
  }
  .eyebrow {
    margin-bottom: 6px;
  }
  .plan {
    font-family: var(--serif);
    font-size: 18px;
  }
  .spend {
    font-family: var(--serif);
    font-size: 34px;
    line-height: 1.1;
    margin-top: 6px;
  }
  .sub {
    color: var(--ink-soft);
    font-size: 13px;
    margin-top: 4px;
  }
  .bar {
    margin-top: 12px;
    height: 6px;
    border-radius: 999px;
    background: var(--paper);
    overflow: hidden;
  }
  .bar span {
    display: block;
    height: 100%;
    background: var(--rooster);
  }
  .stats {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    margin-bottom: 24px;
  }
  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
    border: 1px solid var(--line);
    border-radius: 12px;
    padding: 12px;
    background: var(--cream-2);
    box-shadow: var(--shadow-soft);
  }
  .stat .k {
    font-family: var(--mono);
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--ink-faint);
  }
  .stat .v {
    font-family: var(--serif);
    font-size: 20px;
  }
  @media (min-width: 560px) {
    .stats {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  section {
    margin-top: 24px;
  }
  h2 {
    font-family: var(--mono);
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-soft);
    margin-bottom: 10px;
  }
  .chart {
    display: block;
    width: 100%;
    height: 72px;
  }
  .chart rect {
    fill: var(--rooster);
    opacity: 0.85;
  }
  .rows {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .rows li {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
  }
  .rows .who {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 1px;
  }
  .calls .who {
    gap: 2px;
  }
  .workload {
    font-family: var(--mono);
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .when {
    color: var(--ink-faint);
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    font-family: var(--mono);
    font-size: 11px;
    color: var(--ink-soft);
    white-space: nowrap;
  }
  .cost {
    font-family: var(--mono);
    font-size: 12px;
    min-width: 56px;
    text-align: right;
  }
  .calls li.failed .cost,
  .calls li.failed .meta {
    color: var(--rooster-deep);
  }
  .value {
    font-size: 15px;
  }
  .muted {
    color: var(--ink-soft);
    font-size: 13px;
    margin-top: 8px;
  }
  .hint {
    color: var(--ink-soft);
    font-size: 14px;
  }
  .back {
    display: inline-block;
    margin-top: 28px;
    color: var(--rooster);
    font-family: var(--mono);
    font-size: 12px;
  }
</style>
